import type React from 'react'
import { useEffect, useState } from 'react'
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
  type UniqueIdentifier,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers'
import SortableItem from './sortable-item'

export enum DragListIdKeys {
  uuid = 'uuid',
  id = 'id',
}

type Identifiable = Partial<Record<DragListIdKeys, UniqueIdentifier>>

/**
 * Props for the DraggableList component.
 */
interface DraggableListProps<TItem> {
  /**
   * The list of items to be rendered.
   */
  list: TItem[]

  /**
   * Callback function triggered when the drag operation ends.
   * @param newItems - The updated list of items after the drag operation.
   */
  onDragEnd: (newItems: TItem[]) => void

  /**
   * A function that renders the child component for each item in the list.
   * @param item - The item to be rendered.
   * @returns The React node representing the rendered item.
   */
  children: (item: TItem) => React.ReactNode

  /**
   * The key name used to identify each item in the list.
   * If not provided, the default key name will be used.
   */
  idKeyName?: DragListIdKeys
  className?: string
  showBranchLine?: boolean
}

/**
 * A draggable list component that allows reordering of items.
 *
 * @template TItem - The type of items in the list.
 * @param {DraggableListProps<TItem>} props - The component props.
 * @returns {JSX.Element} - The rendered draggable list component.
 */
export const DraggableList = <TItem extends Identifiable>({
  list,
  onDragEnd,
  children,
  idKeyName = DragListIdKeys.uuid,
  className,
  showBranchLine = false,
}: DraggableListProps<TItem>) => {
  const [items, setItems] = useState(list)

  useEffect(() => {
    setItems(list)
  }, [list])

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (active.id === over?.id) {
      return
    }

    const oldIndex = items.findIndex((item) => item[idKeyName] === active.id)
    const newIndex = items.findIndex((item) => item[idKeyName] === over?.id)
    const newList = arrayMove(items, oldIndex, newIndex)

    setItems(newList)
    onDragEnd(newList)
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={items.map((item) => ({ id: item[idKeyName] || '' }))}
        strategy={verticalListSortingStrategy}
      >
        {items.map((item) => {
          const key = item[idKeyName] || `${item}`
          return (
            <SortableItem className={className} key={key} id={key} showBranchLine={showBranchLine}>
              {children?.(item)}
            </SortableItem>
          )
        })}
      </SortableContext>
    </DndContext>
  )
}
